import React, { useEffect, useState, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import { useDispatch } from 'react-redux';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Modal from '../../../components/modal/modal';
import MultiSelect from '../../../components/inputs/multi-select';
import { getProjectList } from '../../../actions/project-management.action';
import { getProjectType } from '../../projects/utils/getProjectStatus';
import '../../../components/modal/modal.scss';
import './resource-cost-propagation-dialog.scss';

/**
 * Confirmation dialog to select project propagation targets when updating a Cost Level.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Modal visibility flag.
 * @param {Function} props.onClose - Cancel callback.
 * @param {Function} props.onConfirm - Confirm callback receiving selected options.
 * @param {string} [props.levelId] - Cost Level ID to filter mapped projects.
 * @returns {JSX.Element} Rendered confirmation dialog component.
 */
export default function CostLevelPropagationDialog({
  open,
  onClose,
  onConfirm,
  levelId
}) {
  const dispatch = useDispatch();
  const [options, setOptions] = useState({
    useProjects: false,
    useSpecificProjects: false
  });
  const [eligibleProjects, setEligibleProjects] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const selectedActiveProjects = eligibleProjects.filter(
    (p) => selectedProjectIds.includes(p.id) && getProjectType(p).isCurrent
  );
  const hasSelectedActiveProject = selectedActiveProjects.length > 0;
  const hasSelectedQuoteProject = eligibleProjects.some(
    (p) => selectedProjectIds.includes(p.id) && getProjectType(p).isQuote
  );

  const isAllSelected = eligibleProjects.length > 0 && selectedProjectIds.length === eligibleProjects.length;

  const projectOptions = useMemo(() => {
    if (eligibleProjects.length === 0) return [];
    return [
      { label: isAllSelected ? 'Deselect All' : 'Select All', value: 'select_all' },
      ...eligibleProjects.map((p) => ({ label: p.name, value: p.id }))
    ];
  }, [eligibleProjects, isAllSelected]);

  /**
   * Handles project selection change, including Select All and Deselect All toggling.
   *
   * @param {Array<Object>|null} selected - Selected option objects.
   * @param {Object} [actionMeta] - Action metadata from react-select.
   */
  const handleProjectSelect = (selected, actionMeta) => {
    if (!selected || selected.length === 0) {
      setSelectedProjectIds([]);
      return;
    }

    const isSelectAllTriggered =
      actionMeta?.option?.value === 'select_all' ||
      selected.some((s) => s.value === 'select_all');

    if (isSelectAllTriggered) {
      if (isAllSelected) {
        setSelectedProjectIds([]);
      } else {
        setSelectedProjectIds(eligibleProjects.map((p) => p.id));
      }
      return;
    }

    setSelectedProjectIds(
      selected
        .filter((s) => s.value !== 'select_all')
        .map((s) => s.value)
    );
  };

  useEffect(() => {
    if (open) {
      setOptions({
        useProjects: false,
        useSpecificProjects: false
      });
      setSelectedProjectIds([]);
      dispatch(getProjectList({ level: levelId }))
        .then((res) => {
          if (res && Array.isArray(res)) {
            const eligible = res.filter((p) => {
              const { isCurrent, isQuote } = getProjectType(p);
              return (isCurrent || isQuote) && (!levelId || p.level === levelId);
            });
            setEligibleProjects(eligible);
          }
        })
        .catch(() => {});
    }
  }, [open, dispatch, levelId]);

  const handleOptionChange = (option, checked) => {
    setOptions((prev) => {
      const next = { ...prev, [option]: checked };
      if (option === 'useProjects' && checked) {
        next.useSpecificProjects = false;
        setSelectedProjectIds([]);
      } else if (option === 'useSpecificProjects' && checked) {
        next.useProjects = false;
      }
      return next;
    });
  };

  const handleProceed = () => {
    onConfirm({
      ...options,
      selectedProjectIds: options.useSpecificProjects ? selectedProjectIds : []
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="resource-propagation-dialog"
      modalHeading="Cost Level Update Options"
      modalSaveTxt="PROCEED"
      modalCloseTxt="CANCEL"
      onClick={handleProceed}
      modalCloseBtn={onClose}
      showActionBtn
      primary
      savebtnClass="createBtn"
      cxlbtnClass="cancelBtn"
      dataCy="cost-level-propagation-modal"
    >
      <div className="resource-propagation-content mb-3">
        <h6 className="options-group-title mb-3">
          Which of the following do you want the changes to be applied to? Select all that apply:
        </h6>

        <div className="options-container ms-2">
          <Form.Check
            type="checkbox"
            id="propagate-cost-level-all-projects"
            label="All Unsubmitted Quote Projects"
            checked={options.useProjects}
            onChange={(e) => handleOptionChange('useProjects', e.target.checked)}
            className="mb-2"
          />

          <Form.Check
            type="checkbox"
            id="propagate-cost-level-specific-projects"
            label="Select Projects"
            checked={options.useSpecificProjects}
            onChange={(e) => handleOptionChange('useSpecificProjects', e.target.checked)}
            className="mb-2"
          />

          {options.useSpecificProjects && (
            <div className="specific-projects-wrapper">
              <MultiSelect
                placeholder="Select projects..."
                isMulti
                items={projectOptions}
                value={eligibleProjects
                  .filter((p) => selectedProjectIds.includes(p.id))
                  .map((p) => ({ label: p.name, value: p.id }))
                }
                onChangeHandler={handleProjectSelect}
              />
              {hasSelectedActiveProject ? (
                <div className="active-project-warning" data-cy="active-project-warning">
                  <WarningAmberIcon className="active-project-warning-icon" fontSize="small" />
                  <span>
                    Active projects (<strong>{selectedActiveProjects.map((p) => p.name).join(', ')}</strong>) will be updated directly in-place without creating a new revision.
                    {hasSelectedQuoteProject ? ' Selected quote projects will create a new revision.' : ''}
                  </span>
                </div>
              ) : (
                hasSelectedQuoteProject && (
                  <small className="project-revision-notice">
                    Selected quote projects will be updated by creating a new revision.
                  </small>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
